import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { NewFlexContainer } from 'components/common/common.style'
import { unifiedColors } from 'styles/colors'
import { media } from 'styles/responsive'
import { theme, themeUtils } from 'styles/theme'

interface RaceCardContainerProps {
  $noMarginBottom: boolean
  $noMarginTop: boolean
}

export const RaceCardContainer = styled.div<RaceCardContainerProps>`
  display: flex;
  flex-direction: row;
  position: relative;
  background-color: ${theme.palette.surface.default};
  border-radius: 20px;
  padding: 16px 20px;
  margin-bottom: ${({ $noMarginBottom }) => ($noMarginBottom ? '0' : '11px')};
  margin-top: ${({ $noMarginTop }) => ($noMarginTop ? '0' : '20px')};
  box-shadow: 0.5px 1px 0px 0px #0000000d;

  > div:first-of-type {
    flex-grow: 1;
  }

  ${media.maxWidth.md} {
    flex-direction: column;
    height: auto;

    > div:first-of-type {
      flex-grow: 0;
    }
  }

  ${media.maxWidth.sm} {
    padding: 20px;
    flex-direction: column;
  }
`

export const FlexContainer = styled.div`
  display: flex;
  margin: 5px 0 0;
`

interface TypeProps {
  $color: keyof typeof unifiedColors.label
  $backgroundColor: keyof typeof unifiedColors.surface
}

const BaseType = styled.div`
  padding: 2px 6px;
  border-radius: 6px;
  ${themeUtils.label}
  font-weight: ${themeUtils.fontWeights.regular};
  margin-right: 5px;
`

export const Type = styled(BaseType)<TypeProps>`
  color: ${({ $color }) => unifiedColors.label[$color]};
  background-color: ${({ $backgroundColor }) =>
    unifiedColors.surface[$backgroundColor]};
`

export const OutOfStockBadge = styled(BaseType)`
  color: ${unifiedColors.label.onRed};
  background-color: ${unifiedColors.surface.red};
  white-space: nowrap;
`

export const RaceData = styled.div`
  display: flex;
  align-items: center;
  margin: 8px 0 5px;
  width: 100%;
  row-gap: 6px;
  column-gap: 12px;
  flex-wrap: wrap;

  ${media.maxWidth.sm} {
    flex-direction: column;
    align-items: flex-start;
  }
`

export const MultiCourse = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`

export const CountCourse = styled.div`
  display: flex;
  align-items: center;
`

interface StepNumberProps {
  $total: number
  $stepNumber: number
}

export const StepNumber = styled.div<StepNumberProps>`
  position: relative;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: ${unifiedColors.surface.red};
  color: ${unifiedColors.label.onRed};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 10px;
  font-weight: bold;
  font-size: 12px;
  z-index: 10;

  ${({ $total, $stepNumber }) =>
    $stepNumber < $total &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 18px;
        left: 50%;
        transform: translateX(-50%);
        width: 5px;
        height: 14px;
        background-color: ${unifiedColors.surface.red};
      }
    `}
`

export const RaceDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`

interface RaceCardFooterProps {
  $isValidPrice: boolean
  $largerMargin: boolean
}

export const RaceCardFooter = styled.div<RaceCardFooterProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
  margin-left: auto;
  height: auto;

  ${media.maxWidth.md} {
    align-items: flex-start;
    width: 100%;
    margin-top: 10px;
  }
`

export const ContentRaceFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 32px;
  height: 100%;

  ${media.maxWidth.md} {
    width: 100%;
    justify-content: space-between;
  }
`

interface PriceValue {
  $hasReduction?: boolean
  $isRed?: boolean
}
export const PriceValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$hasReduction' && prop !== '$isRed'
})<PriceValue>`
  font-weight: 700;
  width: 1;
  text-align: right;
  ${({ $isRed }) =>
    $isRed &&
    css`
      color: ${theme.palette.label.onRed};
    `}
  ${({ $hasReduction }) =>
    $hasReduction &&
    css`
      text-decoration: line-through;
      text-decoration-color: ${theme.palette.label.default};
      color: ${theme.palette.label.default};
    `}
`

export const RaceCardResumeContainer = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 110px;
  align-items: center;
  gap: 8px;
`

interface RaceCardResumeRaceCardFooterProps {
  $isValidPrice: boolean
  $largerMargin: boolean
}

export const RaceCardResumeRaceCardFooter = styled.div<RaceCardResumeRaceCardFooterProps>`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-left: auto;
  width: 100%;
`

export const RaceCardResumePriceContainer = styled(NewFlexContainer)`
  min-width: 48px;
`

export const AlertBadgeContainer = styled(FlexContainer)`
  position: absolute;
  top: -28px;
  margin: 0 auto;
  left: 50%;
  transform: translateX(-50%);
  ${media.maxWidth.md} {
    top: -12px;
  }
`

export const CtaContainer = styled(NewFlexContainer)`
  align-items: center;
  height: 100%;
  position: relative;
  ${media.maxWidth.md} {
    position: initial;
  }
`
